import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { useI18n } from '../context/i18n';
import './GoldenOpportunities.css';

const API_BASE_URL = 'https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com';

const fallbackChartData = { meta: 50000, proyeccion: 35700, faltan: 14300, actual: 21400, dias: [] };
const getDefaultSummary = (language) => ({
  lift: '$0.00 lift',
  rpr: 'RPR 0.0%',
  activatedActions: language === 'en' ? '0/0 activatable actions' : '0/0 acciones activables'
});
const defaultSnapshot = { totalCustomers: '0', repeatRate: '0.0%', avgAov: '$0', avgFrequency: '0.00x', medianDaysToSecondPurchase: '0' };
const getDefaultStatus = (language) => ({
  statusLabel: language === 'en' ? 'WEEK STATUS' : 'ESTADO DE LA SEMANA',
  statusTitle: language === 'en' ? 'Loading' : 'Cargando',
  statusAlert: language === 'en' ? 'Waiting for data' : 'Esperando datos',
  needValue: '$0.00/day',
  isOnTrack: true
});
const getDefaultSuggestedQuestions = (language) => (
  language === 'en'
    ? ['Which exact segment should I activate first?', 'How do I use ALS to increase repurchase?', 'Which action has the biggest impact on AOV?', 'Give me a 7-day tactical plan']
    : ['Qué segmento exacto debo activar primero', 'Cómo uso ALS para subir la recompra', 'Qué acción tiene mayor impacto en AOV', 'Dame un plan táctico de 7 días']
);

const fmt = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '$0';
  if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

const fmtPct = (v) => `${Number(v || 0).toFixed(1)}%`;

const fixMojibake = (value) => {
  const text = String(value || '');
  try { return decodeURIComponent(escape(text)); } catch { return text; }
};

const normalizeApiTextKey = (value) =>
  fixMojibake(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[¿?¡!.,:;()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const apiTextToEnglish = {
  alta: 'High', media: 'Medium', baja: 'Low', urgente: 'Urgent', alto: 'High', medio: 'Medium',
  'datos limitados': 'Limited data',
  'configura customer_metrics para oportunidades avanzadas': 'Configure customer_metrics for advanced opportunities',
  'por que esta #1': 'Why is this #1?',
  'que pasa si no actuo': 'What happens if I do not act?',
  'que segmento exacto': 'Which exact segment?',
  'sin segmentacion': 'No segmentation',
  'sin segmentación': 'No segmentation',
  'activa oportunidades de recompra': 'Activate repurchase opportunities',
  'recupera clientes inactivos': 'Recover inactive customers',
  'sube ticket con upsell': 'Increase AOV with upsell'
};

const localizeApiText = (value, language) => {
  if (typeof value !== 'string') return value;
  const normalizedText = fixMojibake(value);
  if (language !== 'en') return normalizedText;
  const key = normalizeApiTextKey(normalizedText);
  return apiTextToEnglish[key] || normalizedText;
};

const normalizeStatus = (statusData, language) => {
  const fallback = getDefaultStatus(language);
  const source = { ...fallback, ...(statusData || {}) };

  const normalize = (value) => normalizeApiTextKey(value);
  const labelKey = normalize(source.statusLabel);
  const titleKey = normalize(source.statusTitle);
  const alertKey = normalize(source.statusAlert);

  const statusLabelMap = {
    'estado de la semana': { es: 'ESTADO DE LA SEMANA', en: 'WEEK STATUS' },
    'week status': { es: 'ESTADO DE LA SEMANA', en: 'WEEK STATUS' }
  };
  const statusTitleMap = {
    'en ritmo': { es: 'En ritmo', en: 'On track' },
    'on track': { es: 'En ritmo', en: 'On track' },
    'fuera de ritmo': { es: 'Fuera de ritmo', en: 'Off track' },
    'off track': { es: 'Fuera de ritmo', en: 'Off track' },
    cargando: { es: 'Cargando', en: 'Loading' },
    loading: { es: 'Cargando', en: 'Loading' },
    'datos limitados': { es: 'Datos limitados', en: 'Limited data' },
    'limited data': { es: 'Datos limitados', en: 'Limited data' }
  };
  const statusAlertMap = {
    'buen ritmo de cierre semanal': { es: 'Buen ritmo de cierre semanal', en: 'Strong weekly closing pace' },
    'strong weekly closing pace': { es: 'Buen ritmo de cierre semanal', en: 'Strong weekly closing pace' },
    'acelera para cumplir la meta semanal': { es: 'Acelera para cumplir la meta semanal', en: 'Accelerate to hit the weekly target' },
    'accelerate to hit the weekly target': { es: 'Acelera para cumplir la meta semanal', en: 'Accelerate to hit the weekly target' },
    'esperando datos': { es: 'Esperando datos', en: 'Waiting for data' },
    'waiting for data': { es: 'Esperando datos', en: 'Waiting for data' },
    'configura customer metrics para oportunidades avanzadas': { es: 'Configura customer_metrics para oportunidades avanzadas', en: 'Configure customer_metrics for advanced opportunities' },
    'configura customer_metrics para oportunidades avanzadas': { es: 'Configura customer_metrics para oportunidades avanzadas', en: 'Configure customer_metrics for advanced opportunities' },
    'configure customer metrics for advanced opportunities': { es: 'Configura customer_metrics para oportunidades avanzadas', en: 'Configure customer_metrics for advanced opportunities' },
    'configure customer_metrics for advanced opportunities': { es: 'Configura customer_metrics para oportunidades avanzadas', en: 'Configure customer_metrics for advanced opportunities' }
  };

  const isOnTrack = typeof source.isOnTrack === 'boolean'
    ? source.isOnTrack
    : !(titleKey.includes('fuera') || titleKey.includes('off track'));

  return {
    ...source,
    statusLabel: (statusLabelMap[labelKey] && statusLabelMap[labelKey][language]) || source.statusLabel,
    statusTitle: (statusTitleMap[titleKey] && statusTitleMap[titleKey][language]) || source.statusTitle,
    statusAlert: (statusAlertMap[alertKey] && statusAlertMap[alertKey][language]) || source.statusAlert,
    needValue: language === 'en'
      ? String(fixMojibake(source.needValue)).replace('/día', '/day').replace('/dia', '/day')
      : String(fixMojibake(source.needValue)).replace('/day', '/día'),
    isOnTrack
  };
};

// ─── Lever system ────────────────────────────────────────
const leverColors = {
  'SECOND PURCHASE': '#6366F1',
  'ORDER EXPANSION': '#10B981',
  'REACTIVATION':    '#F59E0B',
  'CALENDAR':        '#8B5CF6',
};

const getLeverType = (title) => {
  const t = (title || '').toUpperCase();
  if (t.includes('SEGUNDA') || t.includes('SECOND') || t.includes('RECOMPRA') || t.includes('2DA')) return 'SECOND PURCHASE';
  if (t.includes('UPSELL') || t.includes('AOV') || t.includes('TICKET') || t.includes('EXPAN') || t.includes('ORDEN')) return 'ORDER EXPANSION';
  if (t.includes('REACTIV') || t.includes('DORMAN') || t.includes('WIN') || t.includes('INACTIV') || t.includes('RECUPER')) return 'REACTIVATION';
  if (t.includes('CALEND') || t.includes('CAMPA') || t.includes('PROMO')) return 'CALENDAR';
  return 'SECOND PURCHASE';
};

// ─── Icons ───────────────────────────────────────────────
const IconUsers = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>);
const IconRepeat = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>);
const IconDollar = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>);
const IconCalendar = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>);
const IconChevronRight = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>);
const IconSearch = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>);

// ─── Skeleton ────────────────────────────────────────────
const Skeleton = ({ width = '100%', height = '16px', borderRadius = '6px' }) => (
  <div className="go-skeleton" style={{ width, height, borderRadius }} />
);

const SkeletonCard = () => (
  <div className="go-skeleton-card">
    <Skeleton width="60%" height="14px" />
    <Skeleton width="40%" height="32px" borderRadius="8px" />
    <Skeleton width="50%" height="12px" />
    <Skeleton width="100%" height="8px" borderRadius="4px" />
    <Skeleton width="70%" height="12px" />
    <Skeleton width="100%" height="36px" borderRadius="8px" />
  </div>
);

// ─── Status Bar ──────────────────────────────────────────
const StatusBar = ({ status, language }) => {
  const companyName = (localStorage.getItem('datasetId') || 'tucketts_analytics')
    .replace('_analytics', '').replace(/_/g, ' ');
  const today = new Date();
  const dow = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((dow + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmtD = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const weekRange = `${fmtD(monday)} – ${fmtD(sunday)}`;
  const isOnTrack = status.isOnTrack ?? true;

  return (
    <div className="rp-status-bar">
      <span className="rp-bar-company">{companyName}</span>
      <span className="rp-bar-sep">·</span>
      <span className="rp-bar-week">{weekRange}</span>
      <span className="rp-bar-spacer" />
      <span className={`rp-bar-pill ${isOnTrack ? 'on-track' : 'off-track'}`}>
        <span className="rp-bar-dot" />
        {isOnTrack
          ? (language === 'en' ? 'On Track' : 'En ritmo')
          : (language === 'en' ? 'Off Track' : 'Fuera de ritmo')}
      </span>
    </div>
  );
};

// ─── Goal Tracker ────────────────────────────────────────
const GoalTracker = ({ chart, status, onViewActions }) => {
  const { language } = useI18n();
  const meta = Number(chart.meta || 0);
  const actual = Number(chart.actual || 0);
  const proyeccion = Number(chart.proyeccion || 0);
  const pctActual = meta > 0 ? Math.min((actual / meta) * 100, 100) : 0;
  const pctProjection = meta > 0 ? Math.min((proyeccion / meta) * 100, 100) : 0;
  const isOnTrack = status.isOnTrack ?? true;

  const sparkData = useMemo(() => {
    if (Array.isArray(chart.dias) && chart.dias.length > 1) {
      return chart.dias.map((d, i) => ({ i, v: Number(d.revenue || d.value || d.amount || 0) }));
    }
    return Array.from({ length: 14 }, (_, i) => ({
      i,
      v: actual > 0 ? actual * Math.pow(i / 13, 0.75) : (i + 1) * 8,
    }));
  }, [chart.dias, actual]);

  return (
    <div className="rp-goal-tracker">
      <div className="rp-goal-main">
        <div className="rp-goal-header">
          <span className="rp-goal-label">{language === 'en' ? 'Annual Goal' : 'Meta Anual'}</span>
          <span className="rp-goal-meta">{fmt(meta)}</span>
        </div>
        <div className="rp-progress-track">
          <div className="rp-progress-fill" style={{ width: `${pctActual}%` }} />
          {pctProjection > 0 && (
            <div className="rp-progress-proj" style={{ left: `${pctProjection}%` }} title={`Proj: ${fmt(proyeccion)}`} />
          )}
        </div>
        <div className="rp-goal-kpis">
          <div className="rp-goal-kpi">
            <span className="rp-kpi-label">{language === 'en' ? 'Current' : 'Actual'}</span>
            <span className="rp-kpi-value">{fmt(actual)}</span>
          </div>
          <div className="rp-goal-kpi">
            <span className="rp-kpi-label">{language === 'en' ? 'Projection' : 'Proyección'}</span>
            <span className="rp-kpi-value">{fmt(proyeccion)}</span>
          </div>
          <div className="rp-goal-kpi">
            <span className="rp-kpi-label">{language === 'en' ? 'Remaining' : 'Faltan'}</span>
            <span className="rp-kpi-value rp-kpi-gap">{fmt(chart.faltan)}</span>
          </div>
        </div>
      </div>

      <div className="rp-goal-side">
        <ResponsiveContainer width="100%" height={80}>
          <LineChart data={sparkData} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
            <Line
              type="monotone"
              dataKey="v"
              stroke="#10B981"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className={`rp-status-pill ${isOnTrack ? 'on-track' : 'off-track'}`}>
            <span className="rp-status-pill-dot" />
            {status.statusTitle}
          </span>
          <p className="rp-status-need">{status.needValue}</p>
          <button className="rp-status-link" onClick={onViewActions}>
            {language === 'en' ? 'View plays →' : 'Ver jugadas →'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Snapshot Grid ───────────────────────────────────────
const snapshotConfig = [
  { key: 'totalCustomers', label: { es: 'Clientes totales', en: 'Total customers' }, Icon: IconUsers },
  { key: 'repeatRate', label: { es: 'Tasa de recurrencia', en: 'Repeat rate' }, Icon: IconRepeat },
  { key: 'avgAov', label: { es: 'Ticket promedio (AOV)', en: 'Avg order value' }, Icon: IconDollar },
  { key: 'medianDaysToSecondPurchase', label: { es: 'Mediana días a 2da compra', en: 'Days to 2nd purchase' }, Icon: IconCalendar },
];

const SnapshotGrid = ({ snapshot, isLoading: loading }) => {
  const { language } = useI18n();
  return (
    <div className="go-snapshot-grid">
      {snapshotConfig.map(({ key, label }) => (
        <div className="go-snapshot-card" key={key}>
          <div className="go-snapshot-info">
            <span className="go-snapshot-label">{label[language] || label.es}</span>
            {loading
              ? <Skeleton width="60%" height="20px" />
              : <strong className="go-snapshot-value">{snapshot?.[key] || '—'}</strong>}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Revenue Play Card ───────────────────────────────────
const RevenuePlay = ({ opportunity, rank, isActivated, onActivate, onAsk, delay }) => {
  const { language } = useI18n();
  const lever = getLeverType(opportunity.title);
  const color = leverColors[lever] || '#6366F1';

  return (
    <div
      className={`rp-play-card ${isActivated ? 'activated' : ''}`}
      style={{ '--lever-color': color, animationDelay: `${delay}ms` }}
    >
      <div className="rp-play-accent" />
      <div className="rp-play-rank">#{rank}</div>
      <div className="rp-play-body">
        <div className="rp-play-top">
          <h3 className="rp-play-title">{opportunity.title}</h3>
          <span
            className="rp-play-lever"
            style={{ color, background: `${color}1A` }}
          >
            {lever}
          </span>
        </div>
        <p className="rp-play-meta">
          {opportunity.segmentCount} {language === 'en' ? 'customers' : 'clientes'} · {opportunity.segment}
        </p>
        {opportunity.description && (
          <p className="rp-play-desc">{opportunity.description}</p>
        )}
        {opportunity.conversionRate && (
          <p className="rp-play-conversion">
            ~{opportunity.expectedConverters} {language === 'en' ? 'expected to convert' : 'conversiones estimadas'} · {Math.round(opportunity.conversionRate * 100)}% {language === 'en' ? 'conversion rate (benchmark)' : 'tasa de conversión (benchmark)'}
          </p>
        )}
      </div>
      <div className="rp-play-right">
        <div>
          <div className="rp-play-value">{opportunity.value}</div>
          <span className="rp-play-value-label">{language === 'en' ? 'est. revenue' : 'revenue est.'}</span>
        </div>
        <div className="rp-play-actions">
          <button
            className="rp-activate-btn"
            disabled={isActivated}
            onClick={() => onActivate(opportunity)}
            style={{ background: isActivated ? '#10B981' : color }}
          >
            {isActivated
              ? (language === 'en' ? '✓ Activated' : '✓ Activada')
              : (language === 'en' ? 'Activate' : 'Activar')}
          </button>
          <button className="rp-ask-btn" onClick={() => onAsk(opportunity)} title={language === 'en' ? 'Ask AI' : 'Preguntar IA'}>
            <IconSearch />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Copilot ─────────────────────────────────────────────
const CopilotSection = ({ suggestedQuestions, question, setQuestion, onSubmit }) => {
  const { language } = useI18n();
  const handleSubmit = () => { if (!question.trim()) return; onSubmit(question.trim()); };
  return (
    <div className="go-copilot">
      <div className="go-copilot-header">
        <span className="go-copilot-dot" />
        <span className="go-copilot-title">{language === 'en' ? 'AI Copilot' : 'Copiloto AI'}</span>
        <span className="go-copilot-badge">Beta</span>
      </div>
      <div className="go-copilot-input-row">
        <input
          type="text"
          placeholder={language === 'en' ? 'Ask why these plays close the gap...' : 'Pregunta por qué estas jugadas cierran el gap...'}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); } }}
        />
        <button className="go-copilot-submit" onClick={handleSubmit}>
          <IconChevronRight /> {language === 'en' ? 'Ask' : 'Preguntar'}
        </button>
      </div>
      <div className="go-copilot-chips">
        {suggestedQuestions.map((q, i) => (
          <button key={i} className="go-chip" onClick={() => setQuestion(q)}>{q}</button>
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────
const GoldenOpportunities = ({ onAskCopilot }) => {
  const { language } = useI18n();
  const [chart, setChart] = useState(fallbackChartData);
  const [summary, setSummary] = useState(getDefaultSummary(language));
  const [snapshot, setSnapshot] = useState(defaultSnapshot);
  const [status, setStatus] = useState(normalizeStatus(getDefaultStatus(language), language));
  const [opportunities, setOpportunities] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [insights, setInsights] = useState([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState(getDefaultSuggestedQuestions(language));
  const [question, setQuestion] = useState('');
  const [activatedOpportunityIds, setActivatedOpportunityIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [alsStats, setAlsStats] = useState(null);
  const [activationFunnel, setActivationFunnel] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [newCustomerRisk, setNewCustomerRisk] = useState([]);
  const copilotRef = useRef(null);
  const opportunitiesRef = useRef(null);

  useEffect(() => {
    const getCompanyId = () => {
      const datasetId = localStorage.getItem('datasetId') || '';
      if (datasetId === 'tucketts_analytics') return 'tucketts';
      if (datasetId === 'saldosahuevo_analytics') return 'saldosahuevo';
      if (datasetId === 'coffeetest_analytics') return 'cfft';
      if (datasetId === 'sap_data_std') return 'sap_data_std';
      return datasetId || 'tucketts';
    };

    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        const companyId = getCompanyId();
        const url = `${API_BASE_URL}/dashboard/golden-opportunities?company_id=${encodeURIComponent(companyId)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        setChart({ ...fallbackChartData, ...(data?.chart || {}) });
        const incomingSummary = data?.summary || getDefaultSummary(language);
        setSummary({
          ...incomingSummary,
          activatedActions: localizeApiText(incomingSummary.activatedActions, language)
        });
        setSnapshot(data?.snapshot || defaultSnapshot);
        setStatus(normalizeStatus(data?.status || getDefaultStatus(language), language));
        setOpportunities(
          Array.isArray(data?.opportunities)
            ? data.opportunities.map((opp) => ({
                ...opp,
                title: localizeApiText(opp.title, language),
                segment: localizeApiText(opp.segment, language),
                time: localizeApiText(opp.time, language),
                confidence: localizeApiText(opp.confidence, language),
                description: localizeApiText(opp.description, language)
              }))
            : []
        );
        setDiagnostics(
          Array.isArray(data?.diagnostics)
            ? data.diagnostics.map((item) => ({
                ...item,
                metric: localizeApiText(item.metric, language),
                gap: localizeApiText(item.gap, language)
              }))
            : []
        );
        setInsights(
          Array.isArray(data?.insights)
            ? data.insights.map((item) => ({
                ...item,
                title: localizeApiText(item.title, language),
                detail: localizeApiText(item.detail, language)
              }))
            : []
        );
        setAlsStats(data?.alsStats || null);
        setActivationFunnel(Array.isArray(data?.activationFunnel) ? data.activationFunnel : []);
        setTopCategories(Array.isArray(data?.topCategories) ? data.topCategories : []);
        setNewCustomerRisk(Array.isArray(data?.newCustomerRisk) ? data.newCustomerRisk : []);
        setSuggestedQuestions(
          Array.isArray(data?.copilot?.suggestedQuestions) && data.copilot.suggestedQuestions.length > 0
            ? data.copilot.suggestedQuestions.map((q) => localizeApiText(q, language))
            : getDefaultSuggestedQuestions(language)
        );
      } catch {
        setError(language === 'en'
          ? 'Could not load opportunities. Please try again.'
          : 'No se pudieron cargar las oportunidades. Intenta de nuevo.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [language]);

  const activationStorageKey = useMemo(() => {
    const ds = localStorage.getItem('datasetId') || 'tucketts_analytics';
    return `goldenActivated:${ds}`;
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(activationStorageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) setActivatedOpportunityIds(parsed);
    } catch { setActivatedOpportunityIds([]); }
  }, [activationStorageKey]);

  useEffect(() => {
    try { localStorage.setItem(activationStorageKey, JSON.stringify(activatedOpportunityIds)); } catch { /* noop */ }
  }, [activatedOpportunityIds, activationStorageKey]);

  const handleActivate = (opp) => {
    if (activatedOpportunityIds.includes(opp.id)) return;
    const next = [...activatedOpportunityIds, opp.id];
    setActivatedOpportunityIds(next);
    const prompt = language === 'en'
      ? `Help me activate: ${opp.title}. Segment: ${opp.segment} (${opp.segmentCount} customers). Top ALS recommended category: ${opp.topCategory || 'N/A'}. Churn probability: ${opp.churnProb || 'N/A'}. Give me a 7-day tactical plan, suggested messages, KPI target, and impact measurement.`
      : `Ayúdame a activar: ${opp.title}. Segmento: ${opp.segment} (${opp.segmentCount} clientes). Categoría recomendada ALS: ${opp.topCategory || 'N/A'}. Churn prob: ${opp.churnProb || 'N/A'}. Dame plan táctico 7 días, mensajes sugeridos, KPI objetivo y cómo medir impacto.`;
    setQuestion(prompt);
    if (typeof onAskCopilot === 'function') onAskCopilot(prompt);
    else copilotRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleAskAI = (opp) => {
    const prompt = language === 'en'
      ? `Explain opportunity "${opp.title}" for ${opp.segment} (${opp.segmentCount} customers). Top ALS category: ${opp.topCategory || 'N/A'}. Churn: ${opp.churnProb || 'N/A'}. Average CLV: ${opp.avgClv || 'N/A'}. Give me concrete steps, priority, effort, and risk.`
      : `Explícame la oportunidad "${opp.title}" para ${opp.segment} (${opp.segmentCount} clientes). Categoría ALS top: ${opp.topCategory || 'N/A'}. Churn: ${opp.churnProb || 'N/A'}. CLV promedio: ${opp.avgClv || 'N/A'}. Dame pasos concretos, prioridad, esfuerzo y riesgo.`;
    setQuestion(prompt);
    if (typeof onAskCopilot === 'function') onAskCopilot(prompt);
    else copilotRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmitCopilot = (prompt) => {
    if (typeof onAskCopilot === 'function') onAskCopilot(prompt);
  };

  const handleViewActions = () => {
    opportunitiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const visibleOpps = opportunities.slice(0, 3);
  const activatedCount = visibleOpps.filter((o) => activatedOpportunityIds.includes(o.id)).length;

  return (
    <div className="golden-opportunities-page">
      <StatusBar status={status} language={language} />
      <GoalTracker chart={chart} status={status} onViewActions={handleViewActions} />
      <SnapshotGrid snapshot={snapshot} isLoading={isLoading} />

      <div className="rp-plays-header" ref={opportunitiesRef}>
        <h2 className="rp-plays-title">
          {language === 'en' ? 'Top 3 Revenue Plays' : 'Top 3 Jugadas de Revenue'}
        </h2>
        <span className="rp-plays-sub">
          {language === 'en' ? 'This week · AI-prioritized' : 'Esta semana · Priorizadas por IA'}
        </span>
      </div>

      {error && <div className="go-error">{error}</div>}

      <div className="rp-plays-list">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
          : visibleOpps.map((opp, idx) => (
              <RevenuePlay
                key={opp.id}
                opportunity={opp}
                rank={idx + 1}
                isActivated={activatedOpportunityIds.includes(opp.id)}
                onActivate={handleActivate}
                onAsk={handleAskAI}
                delay={idx * 80}
              />
            ))
        }
      </div>

      {!isLoading && visibleOpps.length > 0 && (
        <p className="rp-insight-line">
          {language === 'en'
            ? `${activatedCount} of ${visibleOpps.length} plays activated this week.`
            : `${activatedCount} de ${visibleOpps.length} jugadas activadas esta semana.`}
        </p>
      )}

      <div ref={copilotRef}>
        <CopilotSection
          suggestedQuestions={suggestedQuestions}
          question={question}
          setQuestion={setQuestion}
          onSubmit={handleSubmitCopilot}
        />
      </div>
    </div>
  );
};

export default GoldenOpportunities;
